import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink } from "react-router";
import "./AppShell.css";
import { Inline } from "@genius-tracker/design-system";
import type { Locale } from "@genius-tracker/i18n";
import { useLocale } from "@genius-tracker/i18n/react";
import { useTheme } from "@genius-tracker/design-system";
import { useAccess } from "../lib/access.js";
import { useModules } from "../modules/useModules.js";
import { useSession } from "../session/SessionProvider.js";
import { AccountMenu } from "./AccountMenu.js";
// E-BUNDLE-17 — hinter einer Ladeschranke, siehe MobileNavDrawerGate.tsx.
import { MobileNavDrawerGate } from "./MobileNavDrawerGate.js";
import { useIsNarrowChrome } from "./useIsNarrowChrome.js";
// E-BUNDLE-17 — hinter einer Ladeschranke, siehe SubscriptionBannerGate.tsx.
import { SubscriptionBannerGate } from "./SubscriptionBannerGate.js";
import { useTenantReadBlock } from "./useTenantReadBlock.js";
// Sprint 26 Wave B — Global Search palette (Cmd/Ctrl+K). Renders an
// invisible portal until the operator triggers the keyboard shortcut.
// E-BUNDLE-14 — hinter einer Ladeschranke, siehe GlobalSearchPaletteGate.tsx.
import { GlobalSearchPaletteGate } from "../search/GlobalSearchPaletteGate.js";
// Sprint 26 Wave H — mobile-only "Add to home screen" hint banner.
import { PwaInstallHintGate } from "./PwaInstallHintGate.js";
// Sprint 26 Wave K — demo-workspace banner + help drawer + feedback widget.
import { DemoWorkspaceBannerGate } from "./DemoWorkspaceBannerGate.js";
// E-BUNDLE-14 — beide hinter einer Ladeschranke, siehe HelpDrawerGate.tsx
// und FeedbackWidgetGate.tsx.
import { HelpDrawerGate } from "../help/HelpDrawerGate.js";
import { FeedbackWidgetGate } from "../help/FeedbackWidgetGate.js";
// Sprint 29 Wave F — global operations status bar. The extracted
// `<OperationsPills />` (Alerts / Driving / Stopped / Fleet) renders the live
// fleet pulse inside the top bar, gated by `sprint29.globalStatusBar`.
import { useSprint29FlagVisible } from "../lib/sprint29FeatureFlags.js";
// E-BUNDLE-20 — hinter einer Ladeschranke, siehe OperationsPillsGate.tsx.
import { OperationsPillsGate } from "./OperationsPillsGate.js";
// GT-CUSTOMER-NOTIFICATIONS-WEB-INAPP-04 — bell + unread badge.
import { NotificationBell } from "../notifications/NotificationBell.js";
// GT-APP-SHELL-2-NAVIGATION-AND-FLEET-PULSE-01 (DL-122) — App Shell 2.0.
import { AppSidebar } from "./AppSidebar.js";
import { useSidebarCollapsed } from "./sidebarCollapsed.js";
import { BrandLogo } from "./BrandLogo.js";
import { MobileBottomNav } from "./MobileBottomNav.js";
import { LOCALE_FLAGS } from "./localeFlags.js";

/**
 * E-BUNDLE-22 — Ladeschranke vor dem aufgeklappten Sprachmenue.
 *
 * Die Topbar-Schaltflaeche bleibt statisch und zeigt Flagge und Kuerzel sofort;
 * die Auswahlliste entsteht erst nach einem Klick. Sie gehoert deshalb nicht in
 * den Startpfad, sondern wird hier nachgeladen.
 */
const LanguageMenuPanel = lazy(async () => {
  const mod = await import("./LanguageMenuPanel.js");
  return { default: mod.LanguageMenuPanel };
});

/**
 * F-MANDANTENSTATUS1 — Vollseite fuer gesperrte Mandanten. Nur im Sperrfall
 * gebraucht, deshalb nicht im Startpfad des Entry-Chunks (E-BUNDLE-118).
 */
const SubscriptionBlockedPage = lazy(async () => {
  const mod = await import("./SubscriptionBlockedPage.js");
  return { default: mod.SubscriptionBlockedPage };
});

const SIDEBAR_INLINE_SIZE = 264;
/** Desktop icon rail (GT-B-SHELL-NAV1). */
const SIDEBAR_COLLAPSED_INLINE_SIZE = 72;
const CONTENT_MAX_INLINE_SIZE = 1280;
const MOBILE_DRAWER_ID = "app-mobile-drawer";

/**
 * GT-APP-SHELL-2-NAVIGATION-AND-FLEET-PULSE-01 (DL-122) — App Shell 2.0.
 *
 * The chrome owner for every authenticated page. Replaces the old top-heavy pill
 * nav with:
 *
 *   - **Desktop** (`> 1023 px`): a left **sidebar** (primary navigation, via
 *     `AppSidebar`) + a **fleet-pulse top bar** (tenant context + live status +
 *     user controls — no main nav) + the page content column.
 *   - **Mobile / tablet** (`<= 1023 px`): a compact top context bar + the page +
 *     a fixed **bottom navigation** (`MobileBottomNav`) whose "More" opens the
 *     existing `MobileNavDrawer` rendering the full grouped nav (`AppSidebar`).
 *
 * Built on the DL-120 "Calm Intelligence" design system; the single AI focal
 * moment is the Genius Intelligence sidebar entry (DL-121 canonical name). Every
 * route the old nav exposed is preserved — see `navModel.ts`.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useLocale();
  const isNarrowChrome = useIsNarrowChrome();
  const { can } = useAccess();
  // A-W4-WEB1 (W4 Schritt 4) — die Administration im Menue haengt an der
  // gerechneten Rechtemenge, nicht mehr an `hasAdminRole(roles)`.
  // `tenant.admin` tragen genau Tenant-Owner und Tenant-Admin (die beiden
  // Rollen, die `hasAdminRole` im Tenant meinte), `platform.tenants.read`
  // traegt der Platform-Owner. Die vierte Rolle der alten Liste,
  // `platform.admin`, steht in keinem Rollenkatalog und haelt daher kein
  // einziges Recht — fuer sie waere jeder Admin-Aufruf ohnehin 403 gewesen.
  const showAdmin = can("tenant.admin") || can("platform.tenants.read");
  const session = useSession();
  const readsBlocked = useTenantReadBlock(session.subscription);
  // A-MODUL-NAV1 — EIN Aufruf des zentralen Modul-Hooks fuer die ganze Shell.
  // Die Nav-Komponenten bekommen die Sicht als Eigenschaft, damit nicht jede
  // eine eigene Abfrage aufmacht.
  const modules = useModules();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, toggleSidebarCollapsed] = useSidebarCollapsed();

  // Close the mobile drawer whenever we cross into desktop chrome so it can
  // never get stranded open behind the sidebar.
  useEffect(() => {
    if (!isNarrowChrome && drawerOpen) setDrawerOpen(false);
  }, [isNarrowChrome, drawerOpen]);

  // F-MANDANTENSTATUS1 — gesperrte Mandanten sehen keine Seite, auch keine Basis.
  if (readsBlocked)
    return (
      <Suspense fallback={null}>
        <SubscriptionBlockedPage />
      </Suspense>
    );

  // Overlays / portals that render nothing in normal flow. Mounted once.
  const overlays = (
    <>
      <GlobalSearchPaletteGate />
      <PwaInstallHintGate />
      <HelpDrawerGate />
      <FeedbackWidgetGate />
    </>
  );

  if (isNarrowChrome) {
    return (
      <div style={rootStyle}>
        <DemoWorkspaceBannerGate />
        <TopBar isNarrow />
        <SubscriptionBannerGate />
        <main style={{ flex: 1, paddingBlock: "var(--gt-sys-space-5)" }}>
          <div style={mobileContentStyle}>
            <div className="gt-page-card">{children}</div>
          </div>
        </main>
        <MobileBottomNav
          onMoreClick={() => setDrawerOpen(true)}
          moreOpen={drawerOpen}
          drawerId={MOBILE_DRAWER_ID}
          modules={modules}
        />
        <MobileNavDrawerGate
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ariaLabel={t("nav.mobile.aria_label")}
        >
          <div id={MOBILE_DRAWER_ID}>
            <AppSidebar
              showAdmin={showAdmin}
              modules={modules}
              onNavigate={() => setDrawerOpen(false)}
            />
          </div>
        </MobileNavDrawerGate>
        {overlays}
      </div>
    );
  }

  return (
    <div style={rootStyle}>
      <DemoWorkspaceBannerGate />
      <div style={{ display: "flex", flex: 1, minBlockSize: 0 }}>
        <aside
          data-testid="app-shell-sidebar"
          data-collapsed={sidebarCollapsed ? "true" : undefined}
          style={{
            ...sidebarStyle,
            inlineSize: sidebarCollapsed ? SIDEBAR_COLLAPSED_INLINE_SIZE : SIDEBAR_INLINE_SIZE,
          }}
        >
          <AppSidebar
            showAdmin={showAdmin}
            modules={modules}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={toggleSidebarCollapsed}
          />
        </aside>
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minInlineSize: 0 }}>
          <TopBar />
          <SubscriptionBannerGate />
          {/* All desktop pages are full-bleed: the content area is painted with
              the chrome surface colour, and the page card fills it flush to the
              topbar + sidebar — no surrounding black gap (like the GI page). */}
          <main className="gt-shell-main-bleed" style={{ flex: 1, paddingBlock: 0 }}>
            <div className="gt-page-card">{children}</div>
          </main>
        </div>
      </div>
      {overlays}
    </div>
  );
}

/**
 * The fleet-pulse top bar — tenant/fleet context + status + user controls only.
 * No primary navigation (that lives in the sidebar / bottom nav).
 */
function TopBar({ isNarrow = false }: { isNarrow?: boolean }) {
  const { t, locale, setLocale } = useLocale();
  const { resolved, setTheme } = useTheme();
  const session = useSession();
  // Sprint 29.4.1 Codex P1 — the documented `sprint29.globalStatusBar` kill
  // switch. When off, the live fleet-pulse pills are hidden entirely.
  const { visible: sprint29StatusPillsVisible } = useSprint29FlagVisible(
    "sprint29.globalStatusBar",
  );
  const tenantLabel = session.subscription?.displayName ?? session.identity.email;

  return (
    <header style={isNarrow ? topBarNarrowStyle : topBarStyle}>
      <div style={isNarrow ? topBarInnerNarrowStyle : topBarInnerStyle}>
        <Inline justify="space-between" align="center" wrap gap={4}>
          <Inline gap={3} align="center" wrap>
            {isNarrow ? (
              <NavLink to="/" style={mobileBrandStyle} aria-label={t("app.title")}>
                <BrandLogo height={26} />
              </NavLink>
            ) : null}
            <span
              style={isNarrow ? tenantLabelNarrowStyle : tenantLabelStyle}
              title={tenantLabel}
              data-testid="topbar-tenant-display"
            >
              {tenantLabel}
            </span>
            {/* Desktop: the fleet-pulse sits inline in the context row. */}
            {!isNarrow && sprint29StatusPillsVisible ? <OperationsPillsGate /> : null}
          </Inline>

          <Inline gap={2} align="center" wrap>
            <NotificationBell
              style={{
                ...topbarControlStyle,
                inlineSize: TOPBAR_CONTROL_HEIGHT,
                paddingInline: 0,
                color: "var(--gt-sys-text)",
              }}
            />
            <LanguageMenu locale={locale} setLocale={setLocale} />
            <ThemeToggleButton resolved={resolved} setTheme={setTheme} />
            <AccountMenu session={session} />
          </Inline>
        </Inline>

        {/* GT-APP-SHELL-2-POST-DEPLOY-VISUAL-POLISH-01 (DL-123) — on mobile the
            fleet-pulse moves to its own compact, single-row, horizontally-
            scrollable strip so it never wraps to multiple rows and bloats the
            header height. Still flag-gated by sprint29.globalStatusBar. */}
        {isNarrow && sprint29StatusPillsVisible ? (
          <div style={mobilePulseStripStyle} data-testid="topbar-fleet-pulse-strip">
            <OperationsPillsGate wrap={false} />
          </div>
        ) : null}
      </div>
    </header>
  );
}

const rootStyle: React.CSSProperties = {
  minBlockSize: "100dvh",
  background: "var(--gt-sys-bg)",
  display: "flex",
  flexDirection: "column",
};

const sidebarStyle: React.CSSProperties = {
  position: "sticky",
  insetBlockStart: 0,
  alignSelf: "flex-start",
  blockSize: "100dvh",
  inlineSize: SIDEBAR_INLINE_SIZE,
  flexShrink: 0,
  overflowY: "auto",
  background: "var(--gt-sys-surface)",
};

const topBarStyle: React.CSSProperties = {
  position: "sticky",
  insetBlockStart: 0,
  zIndex: 10,
  background: "color-mix(in oklab, var(--gt-sys-surface) 94%, transparent)",
  backdropFilter: "saturate(180%) blur(10px)",
  paddingBlock: "var(--gt-sys-space-3)",
};

// Mobile top bar: a slimmer vertical rhythm than desktop so the compact context
// row + the single-row pulse strip stay short.
const topBarNarrowStyle: React.CSSProperties = {
  ...topBarStyle,
  paddingBlock: "var(--gt-sys-space-2)",
};

// Mobile fleet-pulse strip: one horizontally-scrollable row (the pills render
// with `wrap={false}` so they line up and scroll instead of wrapping).
const mobilePulseStripStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-2)",
  marginBlockStart: "var(--gt-sys-space-2)",
  overflowX: "auto",
  overflowY: "hidden",
  paddingBlockEnd: 2,
  WebkitOverflowScrolling: "touch",
};

const topBarInnerStyle: React.CSSProperties = {
  maxInlineSize: CONTENT_MAX_INLINE_SIZE,
  marginInline: "auto",
  paddingInline: "var(--gt-sys-space-6)",
};

const topBarInnerNarrowStyle: React.CSSProperties = {
  paddingInline: "var(--gt-sys-space-4)",
};

const mobileContentStyle: React.CSSProperties = {
  marginInline: "auto",
  paddingInline: "var(--gt-sys-space-4)",
};

const mobileBrandStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-2)",
  fontSize: 15,
  fontWeight: 600,
  color: "var(--gt-sys-text)",
  textDecoration: "none",
};

const tenantLabelStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  blockSize: 32,
  fontSize: 14,
  fontWeight: 600,
  color: "var(--gt-sys-text)",
  maxInlineSize: 260,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const tenantLabelNarrowStyle: React.CSSProperties = {
  ...tenantLabelStyle,
  fontSize: 13,
  maxInlineSize: 140,
};

// Sprint 29.4.1 owner finding #4 — single shared control style so every TopBar
// control lines up at one centerline.
const TOPBAR_CONTROL_HEIGHT = 32;
const topbarControlStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  paddingBlock: 0,
  paddingInline: 10,
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  borderRadius: "var(--gt-sys-radius-md)",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--gt-sys-border)",
  fontSize: 13,
  lineHeight: 1,
  blockSize: TOPBAR_CONTROL_HEIGHT,
  textDecoration: "none",
  whiteSpace: "nowrap",
};

/**
 * Sprint 29.4 owner-rejects fix #9 — single theme toggle. Sun (switch to light)
 * when dark; moon (switch to dark) when light.
 */
function ThemeToggleButton(props: {
  resolved: "light" | "dark";
  setTheme: (next: "light" | "dark" | "system") => void;
}) {
  const { t } = useLocale();
  const next = props.resolved === "dark" ? "light" : "dark";
  const labelKey = next === "light" ? "theme.toggle.to_light" : "theme.toggle.to_dark";
  return (
    <button
      type="button"
      onClick={() => props.setTheme(next)}
      aria-label={t(labelKey)}
      title={t(labelKey)}
      data-testid="topbar-theme-toggle"
      data-resolved={props.resolved}
      data-next={next}
      style={{
        ...topbarControlStyle,
        appearance: "none",
        cursor: "pointer",
        inlineSize: TOPBAR_CONTROL_HEIGHT,
        paddingInline: 0,
      }}
    >
      {props.resolved === "dark" ? <SunGlyph /> : <MoonGlyph />}
    </button>
  );
}

function SunGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      aria-hidden
      style={{ display: "block", color: "#facc15" }}
    >
      <circle cx="10" cy="10" r="3.5" fill="currentColor" />
      <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <line x1="10" y1="2" x2="10" y2="4" />
        <line x1="10" y1="16" x2="10" y2="18" />
        <line x1="2" y1="10" x2="4" y2="10" />
        <line x1="16" y1="10" x2="18" y2="10" />
        <line x1="4.2" y1="4.2" x2="5.6" y2="5.6" />
        <line x1="14.4" y1="14.4" x2="15.8" y2="15.8" />
        <line x1="4.2" y1="15.8" x2="5.6" y2="14.4" />
        <line x1="14.4" y1="5.6" x2="15.8" y2="4.2" />
      </g>
    </svg>
  );
}

function MoonGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      aria-hidden
      style={{ display: "block", color: "#3b82f6" }}
    >
      <path d="M14.5 12.5 A6 6 0 0 1 7.5 5.5 A6.5 6.5 0 1 0 14.5 12.5 Z" fill="currentColor" />
    </svg>
  );
}

/**
 * Sprint 29.4 owner-rejects fix #8 — custom language menu (native `<select>`
 * strips emoji flags on some platforms).
 */
function LanguageMenu(props: { locale: Locale; setLocale: (next: Locale) => void }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      const root = containerRef.current;
      if (!root) return;
      if (!(e.target instanceof Node) || !root.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);
  const Flag = LOCALE_FLAGS[props.locale];
  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("locale.switcher.label")}
        aria-expanded={open}
        aria-controls="topbar-language-menu"
        aria-haspopup="listbox"
        data-testid="topbar-language-button"
        style={{
          ...topbarControlStyle,
          appearance: "none",
          cursor: "pointer",
          gap: 6,
          paddingInline: 8,
        }}
      >
        <span data-testid="topbar-language-button-flag" aria-hidden>
          {Flag ? <Flag /> : null}
        </span>
        <span style={{ fontWeight: 600 }}>{props.locale.toUpperCase()}</span>
      </button>
      {open ? (
        <Suspense fallback={null}>
          <LanguageMenuPanel
            locale={props.locale}
            onSelect={(next) => {
              props.setLocale(next);
              setOpen(false);
            }}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
